import { type Form as FormEntity } from '@wix/ambassador-forms-v4-form/types';
import { type Logger } from '@wix/fe-essentials/bi';
import type { IHttpClient } from '@wix/fe-essentials/http-client';
import type { i18n } from '@wix/fe-essentials/i18n';
import { type PanoramaClientForComponent } from '@wix/fe-essentials/panorama-client';
import {
  BiContext,
  ConfigContextProvider,
  type Currency,
  type Experiments,
  ExperimentsContext,
  type ExternalData,
  ExternalDataProvider,
  type ForcedState,
  ForcedStateContext,
  FormContext,
  HttpClientContext,
  TranslationContext,
  WixRicosViewerContext,
  AddressFormsContext,
  getAddressForm,
  type AddressTemplate,
  EventsProvider,
  FieldInitialDataContext,
  type FieldInitialData,
  PanoramaContext,
  FocusedFieldProvider,
  ScrollProvider,
  type UserData,
} from '@wix/form-fields';
import { type CountryCode } from '@wix/form-multiline-address';
import { type IHostProps } from '@wix/native-components-infra/dist/es/src/types/types';
import type { WixRicosViewerProps } from '@wix/ricos';
import React, { type ReactNode, useMemo, useContext } from 'react';

import { SubmitStatusProvider } from '../../contexts/submit-status-context';
import { HostContext } from '../../tpa/widget/host-context';
import { useFormBi } from '../form/use-form-bi';

export interface FormViewerProviderProps {
  i18n: i18n;
  httpClient?: IHttpClient;
  bi?: Logger;
  panorama?: PanoramaClientForComponent;
  biFormFillSessionId?: string;
  form: FormEntity;
  translationNamespace?: string;
  isMobile?: boolean;
  isEditor?: boolean;
  isComposer?: boolean;
  isRTL?: boolean;
  compId?: string;
  language: string;
  regionalFormat: string;
  externalData?: ExternalData;
  forcedState?: ForcedState[];
  children: ReactNode;
  instance?: string;
  experiments?: Experiments;
  currency?: Currency;
  userData?: UserData;
  WixRicosViewer: React.ComponentType<WixRicosViewerProps>;
  addressTemplates?: AddressTemplate[];
  atlasBaseUrl?: string;
  fieldInitialData?: FieldInitialData;
  enableAnimations?: boolean;
  baseUrl?: string;
}

export const FormViewerProvider = ({
  i18n,
  bi,
  biFormFillSessionId,
  form,
  isMobile,
  isEditor,
  isComposer,
  isRTL,
  compId,
  language,
  regionalFormat,
  externalData,
  forcedState,
  instance,
  children,
  httpClient,
  panorama,
  experiments,
  translationNamespace,
  currency,
  userData,
  WixRicosViewer,
  addressTemplates,
  atlasBaseUrl,
  fieldInitialData,
  enableAnimations,
  baseUrl,
}: FormViewerProviderProps) => {
  const translations = useMemo(
    () => ({
      t: i18n.getFixedT(language, translationNamespace),
      i18n,
    }),
    [language, i18n, translationNamespace],
  );
  const formBi = useFormBi(form, bi, biFormFillSessionId);
  const host = useContext(HostContext) ?? ({} as IHostProps);

  const config = useMemo(
    () => ({
      language,
      regionalFormat,
      instance,
      isMobile,
      currency,
      atlasBaseUrl,
      userData,
      compId,
      baseUrl,
      isEditor,
      isComposer,
      isRTL,
      enableAnimations,
      style: host?.style,
    }),
    [
      language,
      regionalFormat,
      instance,
      isMobile,
      currency,
      atlasBaseUrl,
      userData,
      compId,
      baseUrl,
      isEditor,
      isComposer,
      isRTL,
      enableAnimations,
      host?.style,
    ],
  );

  const forcedViewState = useMemo(() => forcedState ?? [], [forcedState]);

  return (
    <HttpClientContext.Provider value={{ httpClient }}>
      <PanoramaContext.Provider value={{ panorama }}>
        <TranslationContext.Provider value={translations}>
          <ScrollProvider>
            <FocusedFieldProvider>
              <FieldInitialDataContext.Provider value={fieldInitialData}>
                <BiContext.Provider value={formBi}>
                  <ExternalDataProvider externalData={externalData}>
                    <ConfigContextProvider value={config}>
                      <ForcedStateContext.Provider value={forcedViewState}>
                        <FormContext.Provider value={form}>
                          <AddressFormsContext.Provider
                            value={{
                              getCountryAddressForm: (country?: CountryCode) =>
                                getAddressForm(addressTemplates, country),
                              addressTemplates,
                            }}
                          >
                            <ExperimentsContext.Provider value={experiments}>
                              <EventsProvider>
                                <WixRicosViewerContext.Provider
                                  value={WixRicosViewer}
                                >
                                  <SubmitStatusProvider>
                                    {children}
                                  </SubmitStatusProvider>
                                </WixRicosViewerContext.Provider>
                              </EventsProvider>
                            </ExperimentsContext.Provider>
                          </AddressFormsContext.Provider>
                        </FormContext.Provider>
                      </ForcedStateContext.Provider>
                    </ConfigContextProvider>
                  </ExternalDataProvider>
                </BiContext.Provider>
              </FieldInitialDataContext.Provider>
            </FocusedFieldProvider>
          </ScrollProvider>
        </TranslationContext.Provider>
      </PanoramaContext.Provider>
    </HttpClientContext.Provider>
  );
};
